/**
 * Report Generation Utilities
 * Helper functions for report processing and formatting
 */

import { 
  REPORT_TYPE_DEFINITIONS, 
  REPORT_FORMATS, 
  getReportTypeById,
  getDefaultParameters 
} from '../types/reportTypes';

/**
 * Validate report generation request
 */
export const validateReportRequest = (reportType, data, options = {}) => {
  const errors = [];

  // Validate report type
  const reportDef = getReportTypeById(reportType);
  if (!reportDef) {
    errors.push(`Invalid report type: ${reportType}`);
    return { isValid: false, errors };
  }

  // Validate format
  const format = options.format || REPORT_FORMATS.HTML;
  if (!reportDef.supportedFormats.includes(format)) {
    errors.push(`Format ${format} not supported for ${reportType}`);
  }

  // Validate required data
  if (!data || typeof data !== 'object') {
    errors.push('Report data is required and must be an object');
  }

  // Validate parameters if provided
  if (data.parameters) {
    const paramErrors = validateReportParameters(reportType, data.parameters);
    errors.push(...paramErrors);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings: []
  };
};

/**
 * Validate report parameters against report definition
 */
export const validateReportParameters = (reportType, parameters) => {
  const reportDef = getReportTypeById(reportType);
  const errors = [];

  if (!reportDef || !reportDef.parameters) {
    return errors;
  }

  // Check required parameters
  Object.entries(reportDef.parameters).forEach(([paramName, paramConfig]) => {
    if (paramConfig.required && !parameters[paramName]) {
      errors.push(`Required parameter missing: ${paramName}`);
    }

    // Validate parameter type if provided
    if (parameters[paramName]) {
      const validationError = validateParameterValue(
        paramName, 
        parameters[paramName], 
        paramConfig
      );
      if (validationError) {
        errors.push(validationError);
      }
    }
  });

  return errors;
};

/**
 * Validate individual parameter value
 */
export const validateParameterValue = (paramName, value, paramConfig) => {
  switch (paramConfig.type) {
    case 'date':
      if (!isValidDate(value)) {
        return `Invalid date format for parameter: ${paramName}`;
      }
      break;
    
    case 'number':
      if (isNaN(Number(value))) {
        return `Invalid number format for parameter: ${paramName}`;
      }
      break;
    
    case 'string':
      if (typeof value !== 'string') {
        return `Parameter ${paramName} must be a string`;
      }
      break;
    
    default:
      break;
  }

  return null;
};

/**
 * Check if date string is valid
 */
export const isValidDate = (dateString) => {
  if (!dateString) return false;
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

/**
 * Prepare report data with defaults and validation
 */
export const prepareReportData = (reportType, rawData, userParameters = {}) => {
  const reportDef = getReportTypeById(reportType);
  if (!reportDef) {
    throw new Error(`Unknown report type: ${reportType}`);
  }

  // Merge with default parameters
  const defaultParams = getDefaultParameters(reportType);
  const parameters = { ...defaultParams, ...userParameters };

  // Validate parameters
  const paramErrors = validateReportParameters(reportType, parameters);
  if (paramErrors.length > 0) {
    throw new Error(`Parameter validation failed: ${paramErrors.join(', ')}`);
  }

  return {
    type: reportType,
    data: rawData,
    parameters,
    metadata: {
      reportName: reportDef.name,
      reportDescription: reportDef.description,
      category: reportDef.category,
      generatedAt: new Date().toISOString()
    }
  };
};

/**
 * Generate filename for report based on type and parameters
 */
export const generateReportFilename = (reportType, parameters = {}, format = REPORT_FORMATS.HTML) => {
  const reportDef = getReportTypeById(reportType);
  const reportName = reportDef ? reportDef.name : reportType;
  
  // Clean report name for filename
  const cleanName = reportName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  
  // Add date context if available
  let dateContext = '';
  if (parameters.date) {
    dateContext = `_${parameters.date}`;
  } else if (parameters.startDate && parameters.endDate) {
    dateContext = `_${parameters.startDate}_to_${parameters.endDate}`;
  } else if (parameters.startDate) {
    dateContext = `_from_${parameters.startDate}`;
  }

  // Add timestamp
  const timestamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
  
  // Get file extension
  const extension = getFileExtension(format);
  
  return `${cleanName}${dateContext}_${timestamp}.${extension}`;
};

/**
 * Get file extension for format
 */
export const getFileExtension = (format) => {
  switch (format) {
    case REPORT_FORMATS.HTML:
      return 'html';
    case REPORT_FORMATS.PDF:
      return 'pdf';
    case REPORT_FORMATS.CSV:
      return 'csv';
    case REPORT_FORMATS.EXCEL:
      return 'xlsx';
    case REPORT_FORMATS.JSON:
      return 'json';
    default:
      return 'txt';
  }
};

/**
 * Get MIME type for format
 */
export const getMimeType = (format) => {
  switch (format) {
    case REPORT_FORMATS.HTML:
      return 'text/html';
    case REPORT_FORMATS.PDF:
      return 'application/pdf';
    case REPORT_FORMATS.CSV:
      return 'text/csv';
    case REPORT_FORMATS.EXCEL:
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case REPORT_FORMATS.JSON:
      return 'application/json';
    default:
      return 'text/plain';
  }
};

/**
 * Create download blob for report content
 */
export const createReportBlob = (content, format) => {
  const mimeType = getMimeType(format);
  
  if (format === REPORT_FORMATS.JSON && typeof content === 'object') {
    content = JSON.stringify(content, null, 2);
  }
  
  return new Blob([content], { type: mimeType });
};

/**
 * Trigger download of report
 */
export const downloadReport = (content, reportType, parameters, format) => {
  const blob = createReportBlob(content, format);
  const filename = generateReportFilename(reportType, parameters, format);
  
  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Open report in new window/tab
 */
export const openReportInNewWindow = (content, reportType, format = REPORT_FORMATS.HTML) => {
  if (format !== REPORT_FORMATS.HTML) {
    console.warn('Opening non-HTML reports in new window is not supported');
    return;
  }

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(content);
    newWindow.document.close();
  }
};

/**
 * Calculate report generation estimate
 */
export const estimateGenerationTime = (reportType, dataSize = 'small') => {
  // Base estimates in seconds
  const baseEstimates = {
    'daily-log': { small: 1, medium: 3, large: 8 },
    'revenue': { small: 2, medium: 5, large: 12 },
    'employee-performance': { small: 1, medium: 4, large: 10 },
    'cart-utilization': { small: 2, medium: 6, large: 15 },
    'customer-activity': { small: 1, medium: 3, large: 8 }
  };

  const estimates = baseEstimates[reportType] || { small: 2, medium: 5, large: 10 };
  return estimates[dataSize] || estimates.medium;
};

/**
 * Format report data for preview
 */
export const formatReportPreview = (reportType, data, maxItems = 5) => {
  const reportDef = getReportTypeById(reportType);
  if (!reportDef) return { preview: 'Unknown report type', hasMore: false };

  let preview = '';
  let hasMore = false;

  switch (reportType) {
    case 'daily-log':
      if (data.data && data.data.reservations) {
        const reservations = data.data.reservations.slice(0, maxItems);
        hasMore = data.data.reservations.length > maxItems;
        preview = reservations.map(r => 
          `${r.reservationNumber}: ${r.customer?.firstName} ${r.customer?.lastName} - ${r.status}`
        ).join('\n');
      }
      break;

    case 'employee-performance':
      if (data.data && data.data.performanceData) {
        const employees = data.data.performanceData.slice(0, maxItems);
        hasMore = data.data.performanceData.length > maxItems;
        preview = employees.map(e => 
          `${e.employee?.name}: ${e.totalReservations} reservations, ${e.completionRate}% completion`
        ).join('\n');
      }
      break;

    default:
      preview = `${reportDef.name} - ${reportDef.description}`;
      break;
  }

  return { preview, hasMore };
};

/**
 * Get report complexity score
 */
export const getReportComplexity = (reportType, dataSize) => {
  const complexityScores = {
    'daily-log': 2,
    'revenue': 4,
    'employee-performance': 3,
    'cart-utilization': 5,
    'customer-activity': 3
  };

  const baseScore = complexityScores[reportType] || 3;
  const sizeMultiplier = {
    small: 1,
    medium: 1.5,
    large: 2.5
  };

  return baseScore * (sizeMultiplier[dataSize] || 1);
};

/**
 * Check if report supports real-time data
 */
export const supportsRealTimeData = (reportType) => {
  const realTimeSupported = ['daily-log', 'cart-utilization'];
  return realTimeSupported.includes(reportType);
};

/**
 * Get recommended refresh interval for report
 */
export const getRecommendedRefreshInterval = (reportType) => {
  const intervals = {
    'daily-log': 5 * 60 * 1000, // 5 minutes
    'revenue': 30 * 60 * 1000, // 30 minutes  
    'employee-performance': 60 * 60 * 1000, // 1 hour
    'cart-utilization': 10 * 60 * 1000, // 10 minutes
    'customer-activity': 60 * 60 * 1000 // 1 hour
  };

  return intervals[reportType] || 30 * 60 * 1000; // Default 30 minutes
};